import { useEffect, useMemo, useRef, useState } from 'react';
import { Offcanvas, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LeadCloudFileType, VendorWriteType } from '@econtractor/types';
import type { CloudUploadScope } from 'api/apiCloudflare';
import * as apiVendors from 'api/apiVendors';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import ESpinner from 'components/others/Spinner';
import SolarIcon from 'components/icons/SolarIcon';
import { parseCloudFiles } from 'modules/files';
import { normalizePhoneForSave } from 'utils/helpers/functions/sanitizePhoneInput';
import VendorEditForm from './VendorEditForm';
import VendorFormFiles from './VendorFormFiles';
import { vendorErrMessageKey } from '../model/vendorErrMessage';

interface OffVendorEditProps {
    vendorId: number | null;
    setVendorId: (vendorId: number | null) => void;
    fetchVendors: (force?: boolean) => Promise<void>;
}

const emptyVendor: VendorWriteType = {
    vendorName: '',
    vendorEmail: '',
    vendorPhone: '',
    vendorLegalName: '',
    vendorWebsite: '',
    vendorNote: '',
    vendorLegalAddress: '',
    vendorActualAddress: '',
    vendorWarehouseAddress: '',
};

const toWriteVendor = (source: VendorWriteType): VendorWriteType => ({
    vendorName: source.vendorName || '',
    vendorEmail: source.vendorEmail || '',
    vendorPhone: source.vendorPhone || '',
    vendorLegalName: source.vendorLegalName || '',
    vendorWebsite: source.vendorWebsite || '',
    vendorNote: source.vendorNote || '',
    vendorLegalAddress: source.vendorLegalAddress || '',
    vendorActualAddress: source.vendorActualAddress || '',
    vendorWarehouseAddress: source.vendorWarehouseAddress || '',
});

const OffVendorEdit = ({ vendorId, setVendorId, fetchVendors }: OffVendorEditProps) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { showToast } = useToast();
    const { getSettings } = useSettings();
    const [vendor, setVendor] = useState<VendorWriteType>(emptyVendor);
    const [vendorFiles, setVendorFiles] = useState<LeadCloudFileType[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingVendor, setLoadingVendor] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const refVendorName = useRef<HTMLInputElement>(null);
    const skipFetchAfterCreateRef = useRef(false);
    const isCreate = vendorId === 0;

    const title = useMemo(
        () => (isCreate ? t('edit.titleCreate') : t('edit.titleUpdate')),
        [isCreate, t],
    );

    const handleClose = () => {
        setVendorId(null);
        setVendor(emptyVendor);
        setVendorFiles([]);
        setActiveTab('general');
    };

    const handleChange = (field: keyof VendorWriteType, value: string) => {
        setVendor((prev) => ({ ...prev, [field]: value }));
    };

    const fetchVendor = async (id: number) => {
        setLoadingVendor(true);
        const resVendor = await apiVendors.getVendorById(id);
        setLoadingVendor(false);

        if (!resVendor) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        setVendor(toWriteVendor(resVendor));
        setVendorFiles(parseCloudFiles(resVendor.files));
    };

    const persistVendor = async (close: boolean): Promise<number | false> => {
        setLoading(true);
        try {
            const payload: VendorWriteType = {
                ...vendor,
                vendorName: String(vendor.vendorName || '').trim(),
                vendorEmail: String(vendor.vendorEmail || '').trim() || null,
                vendorPhone: normalizePhoneForSave(vendor.vendorPhone || ''),
            };

            const result = isCreate
                ? await apiVendors.createVendor(payload)
                : await apiVendors.updateVendor(vendorId as number, payload);

            if (!result?.stat) {
                const errKey = vendorErrMessageKey(result?.errCode);
                showToast(errKey ? t(errKey) : t('common:messError'), 'danger');
                return false;
            }

            showToast(t(isCreate ? 'messVendorCreated' : 'messVendorUpdated'), 'success');
            await fetchVendors(true);
            await getSettings();

            const savedId = Number(result.vendorId || vendorId || 0);
            if (close) {
                handleClose();
            } else if (isCreate && savedId > 0) {
                skipFetchAfterCreateRef.current = true;
                setVendorId(savedId);
            }

            return savedId > 0 ? savedId : false;
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        await persistVendor(true);
    };

    const handleEnsureEntity = async (): Promise<CloudUploadScope | false> => {
        if (!String(vendor.vendorName || '').trim()) {
            showToast(t('messEmpty'), 'warning');
            setActiveTab('general');
            setTimeout(() => {
                refVendorName.current?.focus();
            }, 100);
            return false;
        }

        const savedId = await persistVendor(false);
        if (!savedId) return false;
        return { vendorId: savedId };
    };

    const handleFilesChange = async (nextFiles: LeadCloudFileType[]): Promise<boolean> => {
        setVendorFiles(nextFiles);
        return true;
    };

    useEffect(() => {
        if (vendorId === null) return;

        if (skipFetchAfterCreateRef.current) {
            skipFetchAfterCreateRef.current = false;
            return;
        }

        setActiveTab('general');
        if (isCreate) {
            setVendor(emptyVendor);
            setVendorFiles([]);
            setTimeout(() => {
                refVendorName.current?.focus();
            }, 100);
            return;
        }
        void fetchVendor(vendorId);
    }, [vendorId]);

    const formProps = {
        values: vendor,
        loading,
        submitLabel: isCreate ? t('toolAddVendor') : t('common:buttons.save'),
        onChange: handleChange,
        onSubmit: handleSubmit,
    };

    return (
        <Offcanvas show={vendorId !== null} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{title}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {loadingVendor ? (
                    <ESpinner />
                ) : (
                    <div className="off-vendor-tabs d-flex flex-column h-100">
                        <Tabs
                            variant="underline"
                            activeKey={activeTab}
                            onSelect={(tab) => tab && setActiveTab(tab)}
                            className="mb-0 invoice-tab border-bottom px-3"
                        >
                            <Tab
                                eventKey="general"
                                title={
                                    <>
                                        <SolarIcon name="info-circle" className="me-2 fs-14" />
                                        {t('tabGeneral')}
                                    </>
                                }
                            >
                                <div className="d-flex flex-column h-100 pt-3">
                                    <VendorEditForm
                                        {...formProps}
                                        section="general"
                                        nameInputRef={refVendorName}
                                    />
                                </div>
                            </Tab>
                            <Tab
                                eventKey="addresses"
                                title={
                                    <>
                                        <SolarIcon name="pin-angle" className="me-2 fs-14" />
                                        {t('tabAddresses')}
                                    </>
                                }
                            >
                                <div className="d-flex flex-column h-100 pt-3">
                                    <VendorEditForm {...formProps} section="addresses" />
                                </div>
                            </Tab>
                            <Tab
                                eventKey="files"
                                title={
                                    <>
                                        <SolarIcon name="paperclip" className="me-2 fs-14" />
                                        {t('tabFiles')}
                                    </>
                                }
                            >
                                <div className="d-flex flex-column h-100 pt-3">
                                    <div className="off-vendor-edit-content px-3 pb-4">
                                        <VendorFormFiles
                                            vendorId={Number(vendorId || 0)}
                                            files={vendorFiles}
                                            onFilesChange={handleFilesChange}
                                            onEnsureEntity={isCreate ? handleEnsureEntity : undefined}
                                        />
                                    </div>
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffVendorEdit;
